import axios from 'axios'
import Cookies from 'js-cookie'
import { handleTokenExpiration } from '../utils/authHelpers'

// En desarrollo, usar ruta relativa para aprovechar el proxy de Vite
// En producción, usar la variable de entorno VITE_API_URL
// Si VITE_API_URL está configurada y estamos en desarrollo, usar el proxy en lugar de la URL directa
const API_BASE_URL = import.meta.env.DEV 
  ? '/api'  // En desarrollo, siempre usar el proxy
  : (import.meta.env.VITE_API_URL || 'http://localhost:8000/api')

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('auth_token') || localStorage.getItem('auth_token') || localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      // Solo log en desarrollo
      if (import.meta.env.DEV) {
        console.warn('⚠️ No se encontró token de autenticación para la petición:', config.url)
      }
    }
    
    
    // Si es FormData, no establecer Content-Type (axios lo hace automáticamente con el boundary correcto)
    // Esto es importante porque si establecemos Content-Type manualmente, el boundary no se incluirá
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
      // Log en desarrollo para debugging
      if (import.meta.env.DEV) {
        const formDataEntries = Array.from(config.data.entries())
        const files = formDataEntries.filter(([key, value]) => value instanceof File)
        
        console.log('📤 Interceptor: Enviando FormData:', {
          url: config.url,
          method: config.method,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          totalEntries: formDataEntries.length,
          hasFiles: files.length > 0,
          files: files.map(([key, value]) => ({
            field: key,
            name: value.name,
            size: value.size,
            type: value.type
          })),
          textFields: formDataEntries
            .filter(([key, value]) => !(value instanceof File))
            .map(([key, value]) => ({ field: key, value }))
        })
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const currentPath = window.location.pathname
    const isAuthRoute = currentPath === '/login' || currentPath === '/register' || currentPath.startsWith('/auth/')
    const errorUrl = error.config?.url || ''
    const isAuthEndpoint = errorUrl.includes('/auth/login') || errorUrl.includes('/auth/register') || errorUrl.includes('/auth/login-phone')
    
    // Manejar error 429 (Too Many Requests)
    if (error.response?.status === 429) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Demasiados intentos. Por favor, intente más tarde.'
      console.warn('⚠️ Error 429 - Demasiados intentos:', {
        url: errorUrl,
        message: errorMessage
      })
      // No hacer logout ni redirigir, solo rechazar la promesa con el error
      return Promise.reject(error)
    }
    
    // Manejar error 403 (Prohibido - sin permisos para este recurso)
    if (error.response?.status === 403) {
      const message = error.response?.data?.error || error.response?.data?.message || 'No tienes permisos para esta acción'
      if (import.meta.env.DEV) {
        console.warn('⚠️ Error 403 - Sin permisos:', { url: errorUrl, message })
      }
      // Evento opcional para que la UI muestre un mensaje (ej. toast)
      try {
        window.dispatchEvent(new CustomEvent('api:forbidden', { detail: { message, url: errorUrl } }))
      } catch (_) {}
      return Promise.reject(error)
    }

    // Manejar error 401 (No autorizado)
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Token inválido o expirado'
      
      // Si el error viene de un endpoint de autenticación (login, register, etc.)
      // NO hacer logout, solo rechazar la promesa
      if (isAuthEndpoint) {
        console.warn('⚠️ Error 401 en endpoint de autenticación:', {
          url: errorUrl,
          message: errorMessage
        })
        return Promise.reject(error)
      }
      
      // Si estamos en una ruta de autenticación y recibimos 401, no hacer logout
      // (puede ser un intento de login fallido)
      if (isAuthRoute) {
        console.warn('⚠️ Error 401 en ruta de autenticación:', {
          url: errorUrl,
          message: errorMessage
        })
        return Promise.reject(error)
      }
      
      // Solo hacer logout si:
      // 1. NO estamos en una ruta de autenticación
      // 2. NO es un endpoint de autenticación
      // 3. El usuario tiene un token (está "autenticado")
      const hasToken = !!(Cookies.get('auth_token') || localStorage.getItem('auth_token') || localStorage.getItem('token'))
      
      if (hasToken) {
        console.error('❌ Error 401 - Token inválido o expirado:', {
          url: errorUrl,
          message: errorMessage,
          currentPath
        })
        
        // Limpiar tokens
        Cookies.remove('auth_token')
        localStorage.removeItem('auth_token')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        
        // Llamar al handler de logout del AuthContext para actualizar el estado
        // Esto asegura que el usuario sea deslogueado correctamente
        try {
          handleTokenExpiration()
        } catch (err) {
          console.warn('⚠️ No se pudo llamar al logout handler, redirigiendo manualmente:', err)
        }
        
        // Redirigir al login solo si no estamos ya ahí
        // (el logout del authService también redirige, pero por si acaso)
        if (!isAuthRoute) {
          window.location.href = '/login'
        }
      } else {
        // No hay token, probablemente es un error de autenticación normal
        console.warn('⚠️ Error 401 sin token - probablemente intento de acceso no autorizado:', {
          url: errorUrl,
          message: errorMessage
        })
      }
    }
    
    return Promise.reject(error)
  }
)

export default api

